import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input} from '@angular/core';
import {ToastService} from '../toast/toast-service';

@Component({
  selector: 'app-record-code',
  standalone: true,
  template: `
    @if (code) {
      <div class="record-code-wrap d-inline-flex align-items-center gap-1 text-muted">
        <code [class]="variant === 'badge' ? 'record-code-badge' : 'record-code-text'">{{ code }}</code>
        <button type="button"
                class="copy-code-button"
                (click)="copy($event)"
                [attr.aria-label]="copied ? 'คัดลอกแล้ว' : copyTitle"
                [title]="copied ? 'คัดลอกแล้ว' : copyTitle">
          @if (copied) {
            <i class="bi bi-check2 text-success"></i>
          } @else {
            <i class="bi bi-clipboard"></i>
          }
        </button>
      </div>
    } @else {
      <span class="text-muted">{{ fallback }}</span>
    }
  `,
  styles: [`
    :host {
      display: inline-flex;
      vertical-align: middle;
      max-width: 100%;
      min-width: 0;
    }

    .record-code-wrap {
      min-width: 0;
    }

    /* A long code (O-<uuid>) wraps at a hyphen on a narrow card instead of pushing
       the copy button out past the edge (break-word, not anywhere, so the browser
       prefers the hyphen over splitting mid-segment). */
    .record-code-badge,
    .record-code-text {
      min-width: 0;
      overflow-wrap: break-word;
      word-break: normal; /* a host may set break-all on the value column */
    }

    .record-code-badge {
      display: inline-block;
      font-size: 0.78rem;
      //font-weight: 700;
      color: #111827;
      line-height: 1.4;
      letter-spacing: 0.01em;
    }

    .record-code-text {
      font-family: var(--bs-font-monospace, monospace);
      color: var(--bs-secondary-color);
      font-size: 0.85em;
    }

    .copy-code-button {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      color: var(--bs-secondary-color);
      font-size: 0.75rem;
      line-height: 1;
      padding: 0.2rem;
      border-radius: 4px;
      transition: color 0.15s ease, background-color 0.15s ease;

      &:hover {
        color: var(--primary-accent, #0d6efd);
        background-color: #f1f5f9;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordCodeComponent {
  @Input() code?: string | null = null;
  @Input() variant: 'badge' | 'text' = 'badge';
  @Input() fallback = '-';
  @Input() copyTitle = 'คัดลอกรหัสรายการ';
  @Input() toastMessage = 'คัดลอกรหัสรายการแล้ว';

  protected copied = false;
  private copyTimeout: any = null;

  private readonly toast = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected copy(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.code || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard.writeText(this.code).then(() => {
      this.toast.success(this.toastMessage);
      this.copied = true;
      this.cdr.markForCheck();
      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout);
      }
      this.copyTimeout = setTimeout(() => {
        this.copied = false;
        this.cdr.markForCheck();
      }, 2000);
    });
  }
}
